<template>
  <div class="extragas-reportar">
    <!-- NAVBAR -->
    <nav class="eg-navbar">
      <div class="eg-navbar-inner">
        <router-link to="/Extragas-web" class="eg-logo-edge">
          <img src="@/assets/logo/extragas-logo.png" alt="EXTRA GAS" class="eg-logo-edge-img" />
        </router-link>
        <div class="eg-nav-actions">
          <router-link to="/Extragas-web" class="eg-btn eg-btn-outline">
            <Icon icon="mdi:arrow-left" class="text-base" />
            <span class="hidden sm:inline">Volver al Inicio</span>
          </router-link>
        </div>
      </div>
    </nav>

    <!-- CONTENIDO -->
    <div class="eg-content-wrapper">
      <!-- Loading -->
      <div v-if="cargandoPedido" class="eg-loading-state">
        <Icon icon="mdi:loading" class="animate-spin text-5xl text-orange-500" />
        <p class="eg-loading-text">Cargando información del pedido...</p>
      </div>

      <!-- Error -->
      <div v-else-if="!pedido" class="eg-error-state">
        <Icon icon="mdi:clipboard-alert-outline" class="text-6xl text-orange-500" />
        <h2 class="eg-error-title">Pedido no encontrado</h2>
        <p class="eg-error-desc">
          No pudimos encontrar el pedido que intentas pagar.
          Verifica el enlace o consulta tu pedido desde la página principal.
        </p>
        <router-link to="/Extragas-web" class="eg-btn eg-btn-primary eg-btn-large">
          <Icon icon="mdi:home" class="text-lg" />
          Ir al Inicio
        </router-link>
      </div>

      <!-- Formulario -->
      <div v-else class="eg-form-wrapper">
        <div class="eg-hero-header">
          <div class="eg-hero-badge">
            <Icon icon="mdi:file-document-check" class="text-sm" />
            <span>Reportar Pago</span>
          </div>
          <h1 class="eg-hero-title">
            Reporta tu pago para<br>
            <span class="eg-text-gradient">verificar tu pedido</span>
          </h1>
          <p class="eg-hero-desc">
            Realiza tu pago por <strong>Pago Móvil</strong> con los datos de abajo
            y reporta el monto exacto en bolívares.
          </p>
        </div>

        <!-- Datos para hacer el Pago Móvil -->
        <div class="eg-pago-movil-box">
          <div class="eg-pago-movil-header">
            <Icon icon="mdi:cellphone-arrow-down" class="text-2xl" />
            <div>
              <p class="eg-pago-movil-title">Datos de Pago Móvil</p>
              <p class="eg-pago-movil-desc">Transfiere el monto exacto antes de reportar</p>
            </div>
          </div>

          <div class="eg-pago-datos">
            <div class="eg-pago-dato">
              <span class="eg-pago-dato-label">Banco</span>
              <div class="eg-pago-dato-copy">
                <span class="eg-pago-dato-value">Banco de Tesoro</span>
                <button @click="copiarAlPortapapeles('Banco de Tesoro', 'banco')" class="eg-copy-btn">
                  <Icon :icon="copiado === 'banco' ? 'mdi:check' : 'mdi:content-copy'" class="text-sm" />
                </button>
              </div>
            </div>

            <div class="eg-pago-dato">
              <span class="eg-pago-dato-label">Cédula / RIF</span>
              <div class="eg-pago-dato-copy">
                <span class="eg-pago-dato-value">V-26.035.840</span>
                <button @click="copiarAlPortapapeles('26035840', 'cedula')" class="eg-copy-btn">
                  <Icon :icon="copiado === 'cedula' ? 'mdi:check' : 'mdi:content-copy'" class="text-sm" />
                </button>
              </div>
            </div>

            <div class="eg-pago-dato eg-pago-dato-monto">
              <span class="eg-pago-dato-label">Monto a pagar</span>
              <div class="eg-pago-dato-copy">
                <span class="eg-pago-dato-value eg-pago-dato-monto-value">
                  Bs. {{ formatearNumero(pedido.totalBs, 2) }}
                </span>
                <button @click="copiarAlPortapapeles(String(pedido.totalBs || 0), 'monto')" class="eg-copy-btn">
                  <Icon :icon="copiado === 'monto' ? 'mdi:check' : 'mdi:content-copy'" class="text-sm" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Resumen del pedido -->
        <div class="eg-pedido-resumen">
          <div class="eg-pedido-resumen-header">
            <Icon icon="mdi:receipt" class="text-2xl" />
            <div>
              <p class="eg-pedido-resumen-label">Pedido</p>
              <p class="eg-pedido-resumen-numero">#{{ pedido.numeroPedido }}</p>
            </div>
          </div>
          <div class="eg-pedido-resumen-body">
            <div class="eg-pedido-resumen-row">
              <span>Cliente:</span>
              <strong>{{ pedido.nombreCliente }}</strong>
            </div>
            <div class="eg-pedido-resumen-row eg-pedido-resumen-row-total">
              <span>Total a pagar:</span>
              <strong>Bs. {{ formatearNumero(pedido.totalBs, 2) }}</strong>
            </div>
          </div>
        </div>

        <!-- Card del formulario -->
        <div class="eg-form-card">
          <div class="eg-form-header">
            <div class="eg-form-header-icon">
              <Icon icon="mdi:cash-check" class="text-xl" />
            </div>
            <div>
              <h3 class="eg-form-header-title">Reportar mi Pago Móvil</h3>
              <p class="eg-form-header-subtitle">Completa la información</p>
            </div>
          </div>

          <form @submit.prevent="enviarReporte" class="eg-form-body">

            <!-- 1. DATOS DEL PAGADOR -->
            <div class="eg-form-section">
              <div class="eg-form-section-header">
                <div class="eg-form-section-icon eg-icon-blue">
                  <Icon icon="mdi:account-cash" class="text-sm" />
                </div>
                <h4>1. Datos de quien pagó</h4>
              </div>

              <div class="eg-form-grid-2">
                <div>
                  <label class="eg-label">Nombre completo <span class="eg-required">*</span></label>
                  <input
                    v-model="form.nombrePagador"
                    type="text"
                    maxlength="200"
                    required
                    placeholder="Ej: María Pérez"
                    class="eg-input"
                  />
                </div>
                <div>
                  <label class="eg-label">Cédula <span class="eg-required">*</span></label>
                  <input
                    v-model="form.cedulaPagador"
                    type="text"
                    maxlength="20"
                    required
                    placeholder="V-12345678"
                    @input="formatearCedulaPagador"
                    class="eg-input"
                  />
                </div>
              </div>

              <div>
                <label class="eg-label">Teléfono <span class="eg-required">*</span></label>
                <input
                  v-model="form.telefonoPagador"
                  type="tel"
                  maxlength="15"
                  required
                  placeholder="0414-1234567"
                  @input="formatearTelefonoPagador"
                  class="eg-input"
                />
              </div>

              <p class="eg-hint eg-hint-left">
                <Icon icon="mdi:information-outline" class="text-sm inline mr-1" />
                Puede ser el titular del pedido u otra persona que haya hecho el pago.
              </p>
            </div>

            <!-- 2. DATOS DEL PAGO -->
            <div class="eg-form-section">
              <div class="eg-form-section-header">
                <div class="eg-form-section-icon eg-icon-emerald">
                  <Icon icon="mdi:cash-multiple" class="text-sm" />
                </div>
                <h4>2. Monto y datos del pago</h4>
              </div>

              <div class="eg-form-grid-2">
                <div>
                  <label class="eg-label">Monto pagado en Bs. <span class="eg-required">*</span></label>
                  <input
                    v-model.number="form.montoBs"
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    placeholder="0.00"
                    class="eg-input"
                  />
                </div>
                <div>
                  <label class="eg-label">
                    Tasa BCV
                    <span class="eg-tasa-bcv-badge">
                      <Icon icon="mdi:bank" class="text-xs" />
                      Oficial
                    </span>
                  </label>
                  <div class="eg-tasa-bcv-box">
                    <span class="eg-tasa-bcv-value">
                      Bs. {{ formatearNumero(form.tasaBsUsd, 2) }}
                    </span>
                    <Icon icon="mdi:lock" class="text-sm text-slate-400" />
                  </div>
                </div>
              </div>

              <p class="eg-hint eg-hint-left">
                <Icon icon="mdi:information-outline" class="text-sm inline mr-1" />
                La tasa es la oficial del BCV del día. Ingresa el monto exacto en bolívares que pagaste.
              </p>
            </div>

            <!-- 3. BANCO Y REFERENCIA -->
            <div class="eg-form-section">
              <div class="eg-form-section-header">
                <div class="eg-form-section-icon eg-icon-blue">
                  <Icon icon="mdi:cellphone-arrow-down" class="text-sm" />
                </div>
                <h4>3. Datos del Pago Móvil</h4>
              </div>

              <div class="eg-metodo-unico">
                <div class="eg-metodo-unico-icon">
                  <Icon icon="mdi:cellphone-arrow-down" class="text-2xl" />
                </div>
                <div>
                  <p class="eg-metodo-unico-title">Pago Móvil</p>
                  <p class="eg-metodo-unico-desc">Método habilitado para esta recarga</p>
                </div>
                <Icon icon="mdi:check-circle" class="text-2xl text-emerald-500 ml-auto" />
              </div>

              <div>
                <label class="eg-label">Banco emisor <span class="eg-required">*</span></label>
                <select v-model="form.bancoPagador" required class="eg-input eg-select">
                  <option value="" disabled>Selecciona el banco</option>
                  <option v-for="b in bancos" :key="b" :value="b">{{ b }}</option>
                </select>
              </div>

              <div>
                <label class="eg-label">Número de referencia <span class="eg-required">*</span></label>
                <input
                  v-model="form.referencia"
                  type="text"
                  maxlength="50"
                  required
                  placeholder="Ej: 0012345678"
                  class="eg-input"
                />
                <p class="eg-hint eg-hint-left">
                  Copia la referencia que te dio tu banco al hacer el pago
                </p>
              </div>
            </div>

            <!-- 4. NOTAS -->
            <div class="eg-form-section">
              <div class="eg-form-section-header">
                <div class="eg-form-section-icon eg-icon-purple">
                  <Icon icon="mdi:note-text-outline" class="text-sm" />
                </div>
                <h4>4. Notas adicionales</h4>
              </div>

              <textarea
                v-model="form.notas"
                rows="3"
                maxlength="250"
                placeholder="Ej: Pagué desde la cuenta de mi mamá, María Pérez"
                class="eg-input eg-textarea"
              ></textarea>
              <p class="eg-hint">{{ form.notas.length }}/250 caracteres</p>
            </div>

            <!-- Botón enviar -->
            <div class="eg-form-submit">
              <button
                type="submit"
                :disabled="saving || !formularioValido"
                class="eg-btn-submit"
              >
                <Icon
                  :icon="saving ? 'mdi:loading' : 'mdi:send-check'"
                  :class="{ 'animate-spin': saving }"
                  class="text-lg"
                />
                {{ saving ? 'Enviando...' : 'Reportar Pago' }}
              </button>
              <p v-if="!formularioValido" class="eg-form-submit-alert">
                <Icon icon="mdi:alert-circle" class="text-sm inline mr-1" />
                Completa todos los campos obligatorios
              </p>
              <p v-else class="eg-form-submit-hint">
                Un operador verificará tu pago en las próximas horas.
              </p>
            </div>
          </form>
        </div>

        <div class="eg-footer-registro">
          <p>{{ empresa.nombre }} - {{ empresa.rif }}</p>
          <p>{{ empresa.direccion }}, {{ empresa.ciudad }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { obtenerPedidoRecargaPorId, reportarPagoCliente, obtenerTasaActual } from '@/api/gas.js';
import { obtenerDatosEmpresa } from '@/utils/tenantConfig';

export default {
  name: 'ReportarPagoGas',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      cargandoPedido: true,
      pedido: null,
      saving: false,
      copiado: null,
      empresa: {
        nombre: 'EXTRA GAS',
        rif: 'J-41023119-0',
        direccion: 'C.C. LATIN CENTER PB LOCAL 01',
        ciudad: 'ACARIGUA - PORTUGUESA'
      },
      bancos: [
        'Banco de Venezuela',
        'Banco de Tesoro',
        'Banesco',
        'BBVA Provincial',
        'Banco Mercantil',
        'Banco Nacional de Crédito (BNC)',
        'Banco Exterior',
        'Banco Occidental de Descuento (BOD)',
        'Banco del Caribe',
        'Banco Bicentenario',
        'Banco Plaza',
        'Banco Fondo Común',
        'Banco Sofitasa',
        'Banco Activo',
        'Bancamiga',
        'Banco del Sur',
        'Banco Digital de los Trabajadores',
        'Bancrecer',
        'Mi Banco',
        'Otro'
      ],
      form: {
        nombrePagador: '',
        cedulaPagador: '',
        telefonoPagador: '',
        montoBs: 0,
        tasaBsUsd: 0,
        metodoPago: 'pago_movil',
        bancoPagador: '',
        referencia: '',
        notas: ''
      }
    };
  },
  computed: {
    formularioValido() {
      return (
        this.form.nombrePagador?.trim().length >= 3 &&
        this.form.cedulaPagador?.trim().length >= 7 &&
        this.form.telefonoPagador?.replace(/\D/g, '').length >= 10 &&
        this.form.montoBs > 0 &&
        this.form.tasaBsUsd > 0 &&
        this.form.bancoPagador &&
        this.form.referencia &&
        this.form.referencia.trim().length >= 4
      );
    }
  },
  async mounted() {
    this.cargarEmpresa();
    await this.cargarPedido();
  },
  methods: {
    cargarEmpresa() {
      try {
        const datos = obtenerDatosEmpresa();
        if (datos) {
          this.empresa = {
            nombre: datos.nombreComercial || datos.nombre || this.empresa.nombre,
            rif: datos.rif || this.empresa.rif,
            direccion: datos.direccion || this.empresa.direccion,
            ciudad: datos.ciudad || this.empresa.ciudad
          };
        }
      } catch (e) {
        console.warn('No se pudo cargar la config de la empresa');
      }
    },

    async cargarPedido() {
      const idPedido = this.$route.query.pedido;
      if (!idPedido) {
        this.toast.warning('No se especificó un pedido');
        this.cargandoPedido = false;
        return;
      }

      try {
        const res = await obtenerPedidoRecargaPorId(idPedido);
        const pedido = res?.data || res;

        if (!pedido || !pedido.idPedidoRecarga) {
          this.pedido = null;
          return;
        }

        const estadosPermitidos = ['CONFIRMADO', 'PAGO_PENDIENTE'];
        if (!estadosPermitidos.includes(pedido.estado)) {
          this.pedido = null;
          this.toast.warning(`Este pedido ya no puede recibir reportes de pago (estado: ${pedido.estado})`);
          return;
        }

        this.pedido = pedido;

        this.form.montoBs = Number(pedido.totalBs || 0);

        // ⭐ Pre-llenar con datos del cliente si ya tiene
        if (pedido.nombreCliente) {
          this.form.nombrePagador = pedido.nombreCliente;
        }
        if (pedido.cedulaCliente) {
          this.form.cedulaPagador = pedido.cedulaCliente;
        }
        if (pedido.telefonoCliente) {
          this.form.telefonoPagador = pedido.telefonoCliente;
        }

        await this.cargarTasaBcv(pedido);

        if (pedido.referenciaPagoCliente) this.form.referencia = pedido.referenciaPagoCliente;
        if (pedido.notasPagoCliente) this.form.notas = pedido.notasPagoCliente;
      } catch (error) {
        console.error('❌ Error cargando pedido:', error);
        this.pedido = null;
        this.toast.error(error?.message || 'Error al cargar el pedido');
      } finally {
        this.cargandoPedido = false;
      }
    },

    async cargarTasaBcv(pedido) {
      try {
        const tasaRes = await obtenerTasaActual();
        console.log('🔍 Respuesta de obtenerTasaActual:', tasaRes);

        if (!tasaRes?.success || !tasaRes?.data) {
          console.warn('⚠️ obtenerTasaActual falló, usando tasa del pedido');
          const tasaPedido = Number(pedido.tasaBsUsd || 0);
          if (tasaPedido >= 10) {
            this.form.tasaBsUsd = tasaPedido;
            return;
          }
          this.form.tasaBsUsd = 873.87;
          return;
        }

        let tasaFinal = Number(tasaRes.data.precioUsdBs || tasaRes.data.tasaBsUsd || 0);

        if (tasaFinal > 0 && tasaFinal < 1) {
          tasaFinal = Number((1 / tasaFinal).toFixed(2));
        }

        if (tasaFinal < 10) {
          const tasaPedido = Number(pedido.tasaBsUsd || 0);
          if (tasaPedido >= 10) {
            tasaFinal = tasaPedido;
          } else {
            tasaFinal = 873.87;
          }
        }

        this.form.tasaBsUsd = tasaFinal;
      } catch (e) {
        console.error('❌ Error en cargarTasaBcv:', e);
        const tasaPedido = Number(pedido.tasaBsUsd || 0);
        this.form.tasaBsUsd = tasaPedido >= 10 ? tasaPedido : 873.87;
      }
    },

    formatearNumero(valor, decimales = 2) {
      if (valor === null || valor === undefined || valor === '') return '0.00';
      const num = Number(valor);
      if (isNaN(num)) return '0.00';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales
      }).format(num);
    },

    formatearCedulaPagador() {
      let valor = (this.form.cedulaPagador || '').toUpperCase().replace(/[^VEJPG0-9-]/g, '');
      if (!valor) return;
      const letraMatch = valor.match(/^[VEJPG]/);
      const letra = letraMatch ? letraMatch[0] : 'V';
      const numeros = valor.replace(/[^0-9]/g, '');
      if (numeros.length > 0) {
        this.form.cedulaPagador = `${letra}-${numeros}`;
      } else {
        this.form.cedulaPagador = letra;
      }
    },

    formatearTelefonoPagador() {
      let valor = (this.form.telefonoPagador || '').replace(/[^0-9]/g, '');
      if (valor.length > 11) valor = valor.substring(0, 11);
      if (valor.length > 4) {
        this.form.telefonoPagador = `${valor.substring(0, 4)}-${valor.substring(4)}`;
      } else {
        this.form.telefonoPagador = valor;
      }
    },

    async copiarAlPortapapeles(texto, clave) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(texto);
        } else {
          const el = document.createElement('textarea');
          el.value = texto;
          el.style.position = 'fixed';
          el.style.opacity = '0';
          document.body.appendChild(el);
          el.select();
          document.execCommand('copy');
          document.body.removeChild(el);
        }
        this.copiado = clave;
        this.toast.success('Copiado');
        setTimeout(() => {
          if (this.copiado === clave) this.copiado = null;
        }, 2000);
      } catch (err) {
        console.error('Error copiando:', err);
        this.toast.error('No se pudo copiar');
      }
    },

    async enviarReporte() {
      if (!this.formularioValido) {
        this.toast.warning('Completa todos los campos obligatorios');
        return;
      }

      this.saving = true;
      try {
        const montoUsdCalculado = this.form.tasaBsUsd > 0
          ? Number((this.form.montoBs / this.form.tasaBsUsd).toFixed(2))
          : 0;

        const res = await reportarPagoCliente(this.pedido.idPedidoRecarga, {
          montoUsd: montoUsdCalculado,
          montoBs: this.form.montoBs,
          tasaBsUsd: this.form.tasaBsUsd,
          metodoPago: 'pago_movil',
          referencia: this.form.referencia,
          comprobanteUrl: null,
          notas: this.form.notas,

          // ⭐ DATOS DEL PAGADOR
          nombrePagador: this.form.nombrePagador.trim(),
          cedulaPagador: this.form.cedulaPagador.trim(),
          telefonoPagador: this.form.telefonoPagador.trim(),
          bancoPagador: this.form.bancoPagador,
          cuentaPagador: null
        });

        if (res.success) {
          this.toast.success('✅ Pago reportado. En espera de verificación.');

          setTimeout(() => {
            this.$router.push({
              path: '/Extragas-web',
              query: { consultar: 'true' }
            });
          }, 1500);
        } else {
          this.toast.error(res.error || 'Error al reportar el pago');
        }
      } catch (error) {
        console.error('❌ Error en reporte:', error);
        this.toast.error(error?.message || 'Error al enviar el pago');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.extragas-reportar {
  font-family: 'Lexend', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f1a2f;
  min-height: 100vh;
  background: #f0f4fa;
  background-image:
    radial-gradient(circle at 0% 0%, rgba(93, 173, 226, 0.15) 0%, transparent 40%),
    radial-gradient(circle at 100% 100%, rgba(232, 122, 48, 0.12) 0%, transparent 40%);
  background-attachment: fixed;
}

/* NAVBAR */
.eg-navbar {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(15, 26, 47, 0.98);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(232, 122, 48, 0.2);
  min-height: 72px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 20px rgba(15, 26, 47, 0.15);
}

.eg-navbar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 0 1.5rem 0 0;
}

.eg-logo-edge { display: flex; align-items: center; padding: 6px 20px; text-decoration: none; }
.eg-logo-edge-img { height: 62px; width: auto; object-fit: contain; }
.eg-nav-actions { display: flex; align-items: center; gap: 0.5rem; padding-right: 1rem; }

.eg-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.25rem;
  font-size: 0.8rem;
  font-weight: 800;
  border-radius: 0.75rem;
  text-decoration: none;
  transition: all 0.25s ease;
  cursor: pointer;
  border: none;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.eg-btn-primary {
  background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
  color: white;
  box-shadow: 0 6px 20px rgba(232, 122, 48, 0.4);
}
.eg-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(232, 122, 48, 0.55); }

.eg-btn-outline {
  background: rgba(255, 255, 255, 0.06);
  color: white;
  border: 2px solid rgba(93, 173, 226, 0.5);
}
.eg-btn-outline:hover { background: #5DADE2; border-color: #5DADE2; transform: translateY(-2px); }

.eg-btn-large { padding: 1rem 2rem; font-size: 0.9rem; margin-top: 1.5rem; }

/* CONTENIDO */
.eg-content-wrapper { max-width: 720px; margin: 0 auto; padding: 3rem 1.5rem 5rem; }

.eg-loading-state,
.eg-error-state {
  text-align: center;
  padding: 5rem 2rem;
  background: white;
  border-radius: 1.75rem;
  box-shadow: 0 20px 48px -16px rgba(15, 26, 47, 0.15);
}

.eg-loading-text { margin-top: 1.5rem; font-size: 1rem; font-weight: 700; color: #64748b; }
.eg-error-title { font-size: 1.5rem; font-weight: 900; color: #1a2a4a; margin-top: 1.5rem; margin-bottom: 0.5rem; }
.eg-error-desc { font-size: 0.9rem; color: #64748b; line-height: 1.6; max-width: 400px; margin: 0 auto; }

/* HERO */
.eg-hero-header { text-align: center; margin-bottom: 2rem; }

.eg-hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.1rem;
  background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
  color: white;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 1.25rem;
  box-shadow: 0 6px 20px rgba(232, 122, 48, 0.35);
}

.eg-hero-title {
  font-size: 2rem;
  font-weight: 900;
  line-height: 1.15;
  color: #0f1a2f;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
}

.eg-text-gradient {
  background: linear-gradient(135deg, #E87A30 0%, #5DADE2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.eg-hero-desc {
  font-size: 1rem;
  color: #4a5568;
  line-height: 1.6;
  max-width: 500px;
  margin: 0 auto;
  font-weight: 500;
}

@media (min-width: 768px) { .eg-hero-title { font-size: 2.5rem; } }

/* PAGO MÓVIL BOX */
.eg-pago-movil-box {
  background: linear-gradient(135deg, rgba(93, 173, 226, 0.08) 0%, rgba(232, 122, 48, 0.06) 100%);
  border: 2px solid rgba(93, 173, 226, 0.35);
  border-radius: 1.25rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.eg-pago-movil-header {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;
  color: #5DADE2;
}

.eg-pago-movil-title { font-size: 1rem; font-weight: 900; color: #0f1a2f; margin-bottom: 0.15rem; }
.eg-pago-movil-desc { font-size: 0.75rem; color: #64748b; font-weight: 600; }

.eg-pago-datos { display: flex; flex-direction: column; gap: 0.75rem; }
.eg-pago-dato { display: flex; flex-direction: column; gap: 0.35rem; }

.eg-pago-dato-label {
  font-size: 0.7rem;
  font-weight: 900;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.eg-pago-dato-copy {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 0.9rem;
  background: white;
  border: 2px solid rgba(26, 42, 74, 0.08);
  border-radius: 0.75rem;
  transition: all 0.2s ease;
}

.eg-pago-dato-copy:hover { border-color: rgba(232, 122, 48, 0.4); }

.eg-pago-dato-value {
  font-size: 1rem;
  font-weight: 900;
  color: #1a2a4a;
  font-family: 'Lexend', monospace;
  word-break: break-all;
}

.eg-pago-dato-monto-value { color: #E87A30; font-size: 1.15rem; }

.eg-copy-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 0.6rem;
  background: rgba(232, 122, 48, 0.12);
  color: #E87A30;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.eg-copy-btn:hover { background: #E87A30; color: white; transform: scale(1.08); }

/* RESUMEN PEDIDO */
.eg-pedido-resumen {
  background: white;
  border-radius: 1.25rem;
  overflow: hidden;
  border: 2px solid rgba(232, 122, 48, 0.2);
  box-shadow: 0 12px 32px -12px rgba(232, 122, 48, 0.2);
  margin-bottom: 1.5rem;
}

.eg-pedido-resumen-header {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1.15rem 1.5rem;
  background: linear-gradient(135deg, #0f1a2f 0%, #1a2a4a 100%);
  color: white;
}

.eg-pedido-resumen-label {
  font-size: 0.7rem;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.eg-pedido-resumen-numero { font-size: 1.15rem; font-weight: 900; color: #FF9F5A; }

.eg-pedido-resumen-body {
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.eg-pedido-resumen-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  font-size: 0.85rem;
}

.eg-pedido-resumen-row span { color: #64748b; font-weight: 600; }
.eg-pedido-resumen-row strong { color: #1a2a4a; font-weight: 800; }

.eg-pedido-resumen-row-total {
  padding-top: 0.75rem;
  border-top: 2px solid rgba(26, 42, 74, 0.08);
  margin-top: 0.25rem;
  font-size: 0.95rem;
}

.eg-pedido-resumen-row-total strong { color: #E87A30; font-size: 1.35rem; font-weight: 900; }

/* FORM CARD */
.eg-form-card {
  background: white;
  border-radius: 1.75rem;
  overflow: hidden;
  border: 1px solid rgba(15, 26, 47, 0.08);
  box-shadow: 0 30px 60px -20px rgba(15, 26, 47, 0.25);
}

.eg-form-header {
  background: linear-gradient(135deg, #0f1a2f 0%, #1a2a4a 50%, #E87A30 100%);
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
}

.eg-form-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%);
  border-radius: 50%;
}

.eg-form-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.eg-form-header-title {
  font-size: 1.1rem;
  font-weight: 900;
  color: white;
  line-height: 1.2;
  position: relative;
  z-index: 1;
}

.eg-form-header-subtitle {
  font-size: 0.7rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-top: 0.2rem;
  position: relative;
  z-index: 1;
}

.eg-form-body { padding: 2rem 1.75rem; display: flex; flex-direction: column; gap: 2rem; }
.eg-form-section { display: flex; flex-direction: column; gap: 1.15rem; }

.eg-form-section-header {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(15, 26, 47, 0.06);
}

.eg-form-section-icon {
  width: 30px;
  height: 30px;
  border-radius: 0.55rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.eg-icon-emerald { background: linear-gradient(135deg, #27AE60 0%, #2ECC71 100%); box-shadow: 0 4px 12px rgba(39, 174, 96, 0.35); }
.eg-icon-blue { background: linear-gradient(135deg, #5DADE2 0%, #85C1E9 100%); box-shadow: 0 4px 12px rgba(93, 173, 226, 0.35); }
.eg-icon-purple { background: linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%); box-shadow: 0 4px 12px rgba(139, 92, 246, 0.35); }

.eg-form-section-header h4 {
  font-size: 0.8rem;
  font-weight: 900;
  color: #0f1a2f;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.eg-form-grid-2 { display: grid; grid-template-columns: 1fr; gap: 1rem; }
@media (min-width: 640px) { .eg-form-grid-2 { grid-template-columns: 1fr 1fr; } }

.eg-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 900;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.5rem;
}

.eg-required { color: #E87A30; }

.eg-input {
  width: 100%;
  padding: 0.9rem 1.1rem;
  background: #f8fafc;
  border: 2px solid rgba(15, 26, 47, 0.08);
  border-radius: 0.9rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #0f1a2f;
  outline: none;
  transition: all 0.25s ease;
  font-family: inherit;
}

.eg-input:focus {
  border-color: #E87A30;
  background: white;
  box-shadow: 0 0 0 4px rgba(232, 122, 48, 0.15);
}

.eg-input::placeholder { color: #94a3b8; font-weight: 500; }

.eg-select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 2.5rem;
}

.eg-textarea { resize: none; min-height: 80px; }

.eg-hint {
  font-size: 0.7rem;
  color: #94a3b8;
  font-weight: 600;
  margin-top: 0.35rem;
  text-align: right;
}

.eg-hint-left {
  text-align: left;
  margin-top: 0.5rem;
}

/* MÉTODO ÚNICO */
.eg-metodo-unico {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, rgba(93, 173, 226, 0.08) 0%, rgba(39, 174, 96, 0.05) 100%);
  border: 2px solid rgba(93, 173, 226, 0.35);
  border-radius: 1rem;
}

.eg-metodo-unico-icon {
  width: 48px;
  height: 48px;
  border-radius: 0.85rem;
  background: linear-gradient(135deg, #5DADE2 0%, #85C1E9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 6px 16px rgba(93, 173, 226, 0.35);
  flex-shrink: 0;
}

.eg-metodo-unico-title { font-size: 1rem; font-weight: 900; color: #0f1a2f; line-height: 1.2; }
.eg-metodo-unico-desc { font-size: 0.75rem; font-weight: 600; color: #64748b; margin-top: 0.15rem; }

/* TASA BCV */
.eg-tasa-bcv-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  margin-left: 0.35rem;
  padding: 0.15rem 0.45rem;
  background: rgba(39, 174, 96, 0.12);
  color: #15803D;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.eg-tasa-bcv-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.9rem 1.1rem;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 2px solid rgba(39, 174, 96, 0.3);
  border-radius: 0.9rem;
  cursor: not-allowed;
}

.eg-tasa-bcv-value {
  font-size: 1rem;
  font-weight: 900;
  color: #15803D;
  font-family: 'Lexend', monospace;
  letter-spacing: 0.02em;
}

/* SUBMIT */
.eg-form-submit { padding-top: 0.5rem; text-align: center; }

.eg-btn-submit {
  width: 100%;
  padding: 1.1rem 1.75rem;
  background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
  color: white;
  border: none;
  border-radius: 0.95rem;
  font-size: 0.95rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 10px 28px rgba(232, 122, 48, 0.4);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  font-family: inherit;
}

.eg-btn-submit:hover:not(:disabled) { transform: translateY(-3px); box-shadow: 0 14px 36px rgba(232, 122, 48, 0.55); }
.eg-btn-submit:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

.eg-form-submit-hint { font-size: 0.72rem; color: #64748b; margin-top: 0.9rem; text-align: center; font-weight: 500; }
.eg-form-submit-alert { font-size: 0.75rem; color: #E87A30; margin-top: 0.9rem; text-align: center; font-weight: 700; }

/* FOOTER */
.eg-footer-registro {
  text-align: center;
  margin-top: 2.5rem;
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.6;
  font-weight: 500;
}

.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* MÓVIL */
@media (max-width: 480px) {
  .eg-content-wrapper { padding: 2rem 1rem 4rem; }
  .eg-hero-title { font-size: 1.6rem; }
  .eg-form-body { padding: 1.5rem 1.25rem; }
  .eg-form-header { padding: 1.25rem; }
  .eg-form-header-title { font-size: 1rem; }
  .eg-pedido-resumen-numero { font-size: 1rem; }
  .eg-pedido-resumen-row-total strong { font-size: 1.15rem; }
}
</style>